import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { type UserPermissions } from 'pages/users/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PartnerPermissions } from 'pages/partners';
import { User, UsersApi } from 'apis/UsersApi';
import { passMeetsRequirements } from 'utils';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/users/components/Details/styles/details.scss';

type PasswordProps = {
    getAllUsers: (partnerId: number) => void;
    handleUserUpdate: (userDetails: User) => void;
    partnerId?: number;
    permissions: UserPermissions | PartnerPermissions;
    setUserDetails: React.Dispatch<React.SetStateAction<User>>;
    setUserDetailsOriginal: React.Dispatch<React.SetStateAction<User>>;
    userDetails: User;
    userDetailsOriginal: User;
}

export const Password = (props: PasswordProps) => {

    const { userDetails } = props;

    const [state, setState, resetState] = useSuperState({
        confirmNewPassword: '',
        loading: !userDetails,
        newPassword: '',
        newPasswordMeetsRequirements: true,
        oldPassword: '',
        oldPasswordValid: true,
        passwordsMatch: true,
        showConfirmNewPassword: false,
        showModal: false,
        showNewPassword: false,
        showOldPassword: false
    });
    const { confirmNewPassword, loading, newPassword, newPasswordMeetsRequirements, oldPassword, oldPasswordValid,
        passwordsMatch, showConfirmNewPassword, showModal, showNewPassword, showOldPassword } = state;

    const { userId } = useApp();
    const { formatMessage } = useIntl();
    const { createToast } = useNotification();

    const checkNewPasswordsMatch = () => {

        const passwordsMatch = newPassword.trim() === confirmNewPassword.trim();
        setState({ passwordsMatch: passwordsMatch });

        return passwordsMatch;

    };

    const handlePasswordReset = async (e: any) => {

        e.preventDefault();
        e.stopPropagation();

        setState({ oldPasswordValid: true });

        if (!newPasswordMeetsRequirements || !passwordsMatch) return;

        let result;

        const isCurrentUser = userId === userDetails.UserProfileID;

        if (isCurrentUser) {

            result = await UsersApi.ResetCurrentAdminPassword(oldPassword, newPassword);

            if (!result) {

                return setState({ oldPasswordValid: false });

            }

        } else {

            result = await UsersApi.ResetAdminPassword(userDetails.Id, newPassword);

            if (!result) {

                return createToast(formatMessage({ id: 'password_reset_unsuccessfully.b693ad7f', defaultMessage: 'Password Reset Unsuccessfully' }), 'error');

            }

        }

        setState({ showModal: true });

    };

    return (
        <div className="w-50">
            <h3 className="text-strong mb-3">
                <FormattedMessage defaultMessage="Reset Password" id="reset_password.9c0200aa" />
            </h3>
            {loading ? (
                <GMetrixLoader />
            ) : userDetails ? (
                <>
                    <Form className="p-4" onSubmit={e => handlePasswordReset(e)} style={{ width: 300 }}>
                        {userId === userDetails.UserProfileID && (
                        <Form.Group className="mb-3" controlId="oldPasswordInput">
                            <Form.Label className="text-strong fw-semibold">
                                <FormattedMessage defaultMessage="Old Password" id="old_password.98cdef1a" />
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    required
                                    autoComplete='false'
                                    isInvalid={!oldPasswordValid}
                                    onChange={e => setState({ oldPassword: e.target.value })}
                                    placeholder={formatMessage({ id: 'current_password.04af60f8', defaultMessage: 'Current password' })}
                                    size='sm'
                                    type={showOldPassword ? 'text' : 'password'}
                                    value={oldPassword}
                                />
                                <Button
                                    aria-label={showOldPassword ? formatMessage({ id: 'hide_old_password.a228aea5', defaultMessage: 'Hide old password', description: 'aria-label' }) : formatMessage({ id: 'show_old_password.36f22a35', defaultMessage: 'Show old password', description: 'aria-label' })}
                                    className='rounded-end-1'
                                    onClick={() => setState({ showOldPassword: !showOldPassword })}
                                    size='sm'
                                    tabIndex={-1}
                                    variant='secondary'
                                >
                                    <i className={`fa-light ${showOldPassword ? 'fa-eye' : 'fa-eye-slash'}`} ></i>
                                </Button>
                                <Form.Control.Feedback type="invalid">
                                    {formatMessage({ id: 'password_is_incorrect.f558d2c0', defaultMessage: 'Password is incorrect' })}
                                </Form.Control.Feedback>
                            </InputGroup>
                        </Form.Group>
                        )}
                        <Form.Group className="mb-3" controlId="newPasswordInput">
                            <Form.Label className="text-strong fw-semibold">
                                <FormattedMessage defaultMessage="New Password" id="new_password.bdda5f8f" />
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    required
                                    autoComplete='false'
                                    isInvalid={!newPasswordMeetsRequirements}
                                    onBlur={e => setState({ newPasswordMeetsRequirements: passMeetsRequirements(e.target.value) })}
                                    onChange={e => setState({ newPassword: e.target.value })}
                                    size='sm'
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                />
                                <Button
                                    aria-label={showNewPassword ? formatMessage({ id: 'hide_new_password.00da65f3', defaultMessage: 'Hide new password', description: 'aria-label' }) : formatMessage({ id: 'show_new_password.a873e606', defaultMessage: 'Show new password', description: 'aria-label' })}
                                    className='rounded-end-1'
                                    onClick={() => setState({ showNewPassword: !showNewPassword })}
                                    size='sm'
                                    tabIndex={-1}
                                    variant='secondary'
                                >
                                    <i className={`fa-light ${showNewPassword ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                                </Button>
                                <Form.Control.Feedback type="invalid">
                                    <FormattedMessage defaultMessage="Minimum 8 characters, at least one uppercase letter, one lowercase letter, one number, and one special character." id="minimum_8_characters_at_least_one_upperc.02d5146b" />
                                </Form.Control.Feedback>
                            </InputGroup>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="confirmPasswordInput">
                            <Form.Label className="text-strong fw-semibold">
                                <FormattedMessage defaultMessage="Confirm Password" id="confirm_password.e786fc3f" />
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    required
                                    autoComplete='false'
                                    isInvalid={!passwordsMatch}
                                    onBlur={checkNewPasswordsMatch}
                                    onChange={e => setState({ confirmNewPassword: e.target.value })}
                                    placeholder={formatMessage({ id: 'confirm_password.e0b302e2', defaultMessage: 'Confirm password' })}
                                    size='sm'
                                    type={showConfirmNewPassword ? 'text' : 'password'}
                                    value={confirmNewPassword}
                                />
                                <Button
                                    aria-label={showConfirmNewPassword ? formatMessage({ id: 'hide_confirm_new_password.bbb8931a', defaultMessage: 'Hide confirm new password', description: 'aria-label' }) : formatMessage({ id: 'show_confirm_new_password.6b029183', defaultMessage: 'Show confirm new password', description: 'aria-label' })}
                                    className='rounded-end-1'
                                    onClick={() => setState({ showConfirmNewPassword: !showConfirmNewPassword })}
                                    size='sm'
                                    tabIndex={-1}
                                    variant='secondary'
                                >
                                    <i className={`fa-light ${showConfirmNewPassword ? 'fa-eye' : 'fa-eye-slash'}`} ></i>
                                </Button>
                                <Form.Control.Feedback type="invalid">
                                    <FormattedMessage defaultMessage="Confirm Password - Password fields must match." id="confirm_password_password_fields_must_ma.b3b7ad9a" />
                                </Form.Control.Feedback>
                            </InputGroup>
                        </Form.Group>
                        <GMetrixButton
                            type="submit"
                            variant="primary"
                        >
                            <FormattedMessage defaultMessage="Reset Password" id="reset_password.9c0200aa" />
                        </GMetrixButton>
                    </Form>
                    <GMetrixDialog
                        actionButtonClick={resetState}
                        actionButtonDisabled={false}
                        actionButtonText={formatMessage({ id: 'ok.981b7e92', defaultMessage: 'Ok' })}
                        dialogLabel={formatMessage({ id: 'reset_password.9c0200aa', defaultMessage: 'Reset Password' })}
                        onClose={resetState}
                        show={showModal}
                        showCancelButton={false}
                    >
                        <FormattedMessage defaultMessage="Password Reset Successfully" id="password_reset_successfully.4d5010e3" />
                    </GMetrixDialog>
                </>
            ) : (
                <div>
                    <FormattedMessage defaultMessage="Something went wrong while loading the user's details." id="something_went_wrong_while_loading_the_u.53d1cb7a" />
                </div>
            )}
        </div>
    );

};
